import { InfoCircleOutlined } from '@ant-design/icons';
import { Modal, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../api/AuthContext.js';
import GoogleMapsLocationPicker from '../components/GoogleMapsLocationPicker.js';
import ImageUploadCropper from '../components/ImageUploadCropper.js';
import { createVenue } from '../api/createVenue.js';
import { deleteVenue } from '../api/deleteVenue.js';
import { fetchVenuesByOwner } from '../api/fetchVenues.js';
import { fetchVenueStats } from '../api/fetchVenueDetails.js';
import { updateVenue } from '../api/updateVenue.js';
import { showToast, updateToast } from '../utils/toastUtils.js';

function Venues() {
    const auth = useAuth();
    const navigate = useNavigate();

    const [venues, setVenues] = useState(null);
    const [filteredVenues, setFilteredVenues] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [venueStats, setVenueStats] = useState({});
    const [loadingStats, setLoadingStats] = useState(false);

    // Modal states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [currentVenue, setCurrentVenue] = useState(null);
    const [venueName, setVenueName] = useState('');
    const [venueImage, setVenueImage] = useState('');
    const [venueLocationData, setVenueLocationData] = useState({
        latitude: null,
        longitude: null,
    });

    // Fetch all venues
    const getAllVenues = async () => {
        try {
            const data = await fetchVenuesByOwner(auth.currentUser.user_id);
            setVenues(data);
            setFilteredVenues(data);
            
            // Fetch stats for each venue
            if (data && data.length > 0) {
                fetchAllStats(data);
            }
        } catch (err) {
            console.error('Error fetching venues:', err);
            showToast('Failed to load venues', auth.currentUser.email, { type: 'error' });
        }
    };

    // Fetch statistics for all venues
    const fetchAllStats = async (venuesList) => {
        setLoadingStats(true);
        const statsMap = {};
        
        for (const venue of venuesList) {
            try {
                const stats = await fetchVenueStats(venue.venue_id);
                statsMap[venue.venue_id] = stats;
            } catch (err) {
                console.error(`Error fetching stats for ${venue.venue_id}:`, err);
            }
        }
        
        setVenueStats(statsMap);
        setLoadingStats(false);
    };

    useEffect(() => {
        getAllVenues();
    }, []);

    // Search filter
    useEffect(() => {
        if (!venues) return;
        
        if (searchText.trim() === '') {
            setFilteredVenues(venues);
        } else {
            const filtered = venues.filter((v) =>
                v.name.toLowerCase().includes(searchText.toLowerCase()) ||
                (v.location && v.location.toLowerCase().includes(searchText.toLowerCase()))
            );
            setFilteredVenues(filtered);
        }
    }, [searchText, venues]);

    // Modal handlers
    const showCreateModal = () => {
        setModalMode('create');
        setVenueName('');
        setVenueImage('');
        setVenueLocationData({
            latitude: null,
            longitude: null,
        });
        setCurrentVenue(null);
        setIsModalVisible(true);
    };

    const showEditModal = (venue) => {
        setModalMode('edit');
        setVenueName(venue.name);
        setVenueImage(venue.image_url || '');
        setVenueLocationData({
            latitude: venue.latitude || null,
            longitude: venue.longitude || null,
        });
        setCurrentVenue(venue);
        setIsModalVisible(true);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setVenueName('');
        setVenueImage('');
        setVenueLocationData({
            latitude: null,
            longitude: null,
        });
        setCurrentVenue(null);
    };

    const handleModalOk = async () => {
        if (!venueName.trim()) {
            showToast('Venue name is required', auth.currentUser.email, { type: 'error' });
            return;
        }

        const toastId = showToast(
            modalMode === 'create' ? 'Creating venue...' : 'Updating venue...',
            auth.currentUser.email,
            { toastId: modalMode === 'create' ? 'toastIdCreateVenue' : 'toastIdUpdateVenue' }
        );

        try {
            if (modalMode === 'create') {
                await createVenue({
                    owner_id: auth.currentUser.user_id,
                    name: venueName,
                    image_url: venueImage || null,
                    latitude: venueLocationData.latitude,
                    longitude: venueLocationData.longitude,
                });

                updateToast(toastId, auth.currentUser.email, {
                    render: 'Venue created successfully',
                    type: 'success',
                    isLoading: false,
                    autoClose: 1500,
                });
            } else {
                await updateVenue(
                    currentVenue.venue_id,
                    auth.currentUser.user_id,
                    venueName,
                    venueImage || null,
                    venueLocationData.latitude,
                    venueLocationData.longitude
                );

                updateToast(toastId, auth.currentUser.email, {
                    render: 'Venue updated successfully',
                    type: 'success',
                    isLoading: false,
                    autoClose: 1500,
                });
            }

            setIsModalVisible(false);
            setVenueName('');
            setVenueImage('');
            setVenueLocationData({
                latitude: null,
                longitude: null,
            });
            setCurrentVenue(null);
            getAllVenues();
        } catch (err) {
            console.error('Error saving venue:', err);
            updateToast(toastId, auth.currentUser.email, {
                render: modalMode === 'create' ? 'Failed to create venue' : 'Failed to update venue',
                type: 'error',
                isLoading: false,
                autoClose: 2000,
            });
        }
    };

    const handleDeleteVenue = async (venue) => {
        const toastId = showToast('Deleting venue...', auth.currentUser.email, {
            toastId: 'toastIdDeleteVenue',
        });

        try {
            await deleteVenue(venue.venue_id);

            updateToast(toastId, auth.currentUser.email, {
                render: 'Venue deleted successfully',
                type: 'success',
                isLoading: false,
                autoClose: 1500,
            });

            getAllVenues();
        } catch (err) {
            console.error('Error deleting venue:', err);
            updateToast(toastId, auth.currentUser.email, {
                render: 'Failed to delete venue',
                type: 'error',
                isLoading: false,
                autoClose: 2000,
            });
        }
    };

    const handleViewVenue = (venue) => {
        navigate(`/admin/venues/${venue.venue_id}`);
    };

    if (!auth.currentUser) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px'
            }}>
                <div>
                    <h1 style={{
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#1f2937',
                        margin: 0,
                        marginBottom: '8px'
                    }}>
                        Venues
                    </h1>
                    <p style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        margin: 0
                    }}>
                        Manage your event venues and locations
                    </p>
                </div>
                <button
                    onClick={showCreateModal}
                    className="button is-primary"
                    style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}
                >
                    + Create Venue
                </button>
            </div>

            {/* Search Bar */}
            <div style={{ marginBottom: '20px' }}>
                <input
                    type="text"
                    className="input"
                    placeholder="Search venues by name or location..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{
                        maxWidth: '400px',
                        padding: '10px 12px',
                        fontSize: '14px'
                    }}
                />
            </div>

            {/* Venues Table */}
            <div style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden'
            }}>
                {filteredVenues && filteredVenues.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{
                                backgroundColor: '#f9fafb',
                                borderBottom: '2px solid #e5e7eb'
                            }}>
                                <th style={{
                                    padding: '8px 12px',
                                    textAlign: 'left',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>Venue Name</th>
                                <th style={{
                                    padding: '8px 12px',
                                    textAlign: 'left',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>Location</th>
                                <th style={{
                                    padding: '8px 12px',
                                    textAlign: 'center',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    width: '100px'
                                }}>Events</th>
                                <th style={{
                                    padding: '8px 12px',
                                    textAlign: 'center',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    width: '120px'
                                }}>Participants</th>
                                <th style={{
                                    padding: '8px 12px',
                                    textAlign: 'right',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    width: '200px'
                                }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVenues.map((venue) => {
                                const stats = venueStats[venue.venue_id] || {};
                                return (
                                    <tr
                                        key={venue.venue_id}
                                        style={{ borderBottom: '1px solid #e5e7eb' }}
                                    >
                                        <td style={{ padding: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                {venue.image_url ? (
                                                    <img
                                                        src={venue.image_url}
                                                        alt={venue.name}
                                                        style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '4px',
                                                            objectFit: 'cover',
                                                            border: '1px solid #e5e7eb'
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        borderRadius: '4px',
                                                        background: '#f3f4f6',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '18px',
                                                        border: '1px solid #e5e7eb'
                                                    }}>
                                                        📍
                                                    </div>
                                                )}
                                                <span style={{ fontWeight: '500', color: '#1f2937' }}>
                                                    {venue.name}
                                                </span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '12px', color: '#6b7280' }}>
                                            {venue.latitude && venue.longitude ? (
                                                <a
                                                    href={`https://www.google.com/maps/place/${venue.latitude},${venue.longitude}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: '#6b7280',
                                                        textDecoration: 'none',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        fontSize: '12px',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    📍 {venue.name}
                                                </a>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            {loadingStats ? (
                                                <span style={{ color: '#9ca3af', fontSize: '12px' }}>...</span>
                                            ) : (
                                                <span style={{
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#1f2937'
                                                }}>
                                                    {stats.total_events || 0}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            {loadingStats ? (
                                                <span style={{ color: '#9ca3af', fontSize: '12px' }}>...</span>
                                            ) : (
                                                <span style={{
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#1f2937'
                                                }}>
                                                    {stats.total_participants || 0}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'right' }}>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                <button
                                                    onClick={() => handleViewVenue(venue)}
                                                    className="button is-small is-light"
                                                    style={{ fontSize: '12px' }}
                                                >
                                                    View
                                                </button>
                                                <button
                                                    onClick={() => showEditModal(venue)}
                                                    className="button is-small is-light"
                                                    style={{ fontSize: '12px' }}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteVenue(venue)}
                                                    className="button is-small is-light"
                                                    style={{ fontSize: '12px', color: '#dc2626' }}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <div style={{
                        padding: '60px 20px',
                        textAlign: 'center',
                        color: '#6b7280'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📍</div>
                        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                            {searchText ? 'No venues found' : 'No venues yet'}
                        </div>
                        <div style={{ fontSize: '14px', marginBottom: '16px' }}>
                            {searchText ? 'Try a different search term' : 'Create your first venue to get started'}
                        </div>
                        {!searchText && (
                            <button
                                onClick={showCreateModal}
                                className="button is-primary"
                                style={{ marginTop: '8px' }}
                            >
                                + Create Venue
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            <Modal
                title={modalMode === 'create' ? 'Create New Venue' : 'Edit Venue'}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText={modalMode === 'create' ? 'Create' : 'Update'}
                cancelText="Cancel"
            >
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Venue Name <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input
                        type="text"
                        className="input"
                        placeholder="Enter venue name"
                        value={venueName}
                        onChange={(e) => setVenueName(e.target.value)}
                        style={{ width: '100%' }}
                    />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <GoogleMapsLocationPicker
                        key={`${modalMode}-${currentVenue?.venue_id || 'new'}-${isModalVisible}`}
                        locationData={venueLocationData}
                        onChange={setVenueLocationData}
                    />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Venue Image
                    </label>
                    <ImageUploadCropper
                        value={venueImage}
                        onChange={setVenueImage}
                        aspectRatio={1}
                        cropShape="rect"
                    />
                </div>
            </Modal>
        </div>
    );
}

export default Venues;

